<template>
  <section>
    <div class="company-tabs" role="group" aria-label="Саппортер">
      <Button
        v-for="(company, index) in tabCompanies"
        :key="company.id"
        :variant="companyTab === index ? 'default' : 'ghost'"
        size="sm"
        :aria-pressed="companyTab === index"
        @click="companyTab = index"
      >
        {{ company.name }}
      </Button>
    </div>
    <div class="property-grid">
      <property-card v-for="o in items.results" :key="o.id" :property="o" />
    </div>
    <div v-show="!busyOrDisabled" ref="loadMoreTrigger" class="load-more-trigger" />
    <div v-if="loading" class="ui-empty">
      <Spinner />
      <span>Загрузка клиентов…</span>
    </div>
    <p v-else-if="!items.results.length" class="ui-empty">Клиенты не найдены</p>
  </section>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import axios from 'axios';
import { useRouter, useRoute } from 'vue-router';
import { useMainStore } from '../store';
import PropertyCard from './PropertyCard.vue';

const router = useRouter();
const route = useRoute();
const store = useMainStore();

const companyTab = ref(0);
const items = ref({
  count: 0,
  next: '',
  previous: '',
  results: [] as any[],
});
const tabCompanies = ref<Company[]>([]);
const busyOrDisabled = ref(false);
const limit = 30;
const prevUri = ref('');
const loading = ref(false);
const loadMoreTrigger = ref<HTMLElement | null>(null);
let scrollObserver: IntersectionObserver | null = null;

watch(companyTab, async (val) => {
  let query: any;
  if (val != null && val != 0) {
    query = {
      q: store.query,
      supporter: tabCompanies.value[val].id.toString(),
    };
  } else {
    if (store.query != '') {
      query = {
        q: store.query,
      };
    } else {
      query = null;
    }
  }

  await router.push({
    name: 'home',
    query,
  });
  clearItems();
});

// Watch store query changes
watch(
  () => store.getQuery(),
  () => {
    clearItems();
  }
);

function clearItems() {
  items.value.count = 0;
  items.value.next = '';
  items.value.previous = '';
  items.value.results = [];
  loadMore();
}

async function loadMore() {
  const supporterId = route.query.supporter;
  busyOrDisabled.value = true;
  loading.value = true;

  try {
    if (items.value.count != 0 && (items.value.next == '' || items.value.next == null)) {
      busyOrDisabled.value = true;
      return;
    }

    let offsetParam: any = null;
    if (items.value.next != '') {
      const urlNextString = new URL(items.value.next);
      offsetParam = urlNextString.searchParams.get('offset');
    }

    const request = {
      url: '/api/properties',
      params: {
        limit: limit,
        supporters__supporter: supporterId,
        offset: offsetParam,
        search: store.query == '' ? null : store.query,
      },
    };

    const uri = axios.getUri(request);

    if (uri == prevUri.value) {
      return;
    }

    const response = (await axios.get(uri)).data as PagedList<Property>;

    items.value.count = response.count;
    items.value.next = response.next == null ? '' : response.next;
    items.value.previous = response.previous == null ? '' : response.previous;
    if (response.results) {
      for (const prop of response.results) {
        if (prop) {
          items.value.results.push(prop);
        }
      }
    }

    prevUri.value = uri;
    busyOrDisabled.value = false;
  } finally {
    loading.value = false;
  }
}

// Setup intersection observer for infinite scroll
onMounted(async () => {
  await initCompanies();
  if (!loading.value) await loadMore();

  // Simple infinite scroll implementation
  if (loadMoreTrigger.value) {
    scrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !busyOrDisabled.value) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );
    scrollObserver.observe(loadMoreTrigger.value);
  }
});

onBeforeUnmount(() => scrollObserver?.disconnect());

async function initCompanies() {
  const companies = await store.getCompanies();

  const allTabCompany = {
    id: 0,
    name: 'Все',
  } as Company;
  tabCompanies.value.push(allTabCompany);
  companies.forEach((element) => {
    tabCompanies.value.push(element);
  });
  tabCompanies.value = tabCompanies.value.sort((x, y) => (x.id > y.id ? 1 : -1));

  const currentSupporterFilter = route.query['supporter'];
  if (currentSupporterFilter) {
    const filterId = Number(currentSupporterFilter);
    const filterCompany = tabCompanies.value.find((x) => x.id == filterId);
    if (filterCompany) {
      const index = tabCompanies.value.indexOf(filterCompany);
      companyTab.value = index;
    }
  }
}
</script>
